import { useQuery } from '@tanstack/react-query';
import { Children, FormEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useUpdateStatisticsMutation } from 'api/adminApi';
import {
  getEquipList,
  getPartList,
  getSectionList,
  getZoneList,
  useEditDeviceMutation,
  useEditEquipMutation,
  useRegisterDeviceMutation,
  useRegisterEquipMutation,
} from 'api/deviceApi';
import {
  DeviceInfoRequest,
  DeviceSearchRequest,
  EquipmentInfoRequest,
} from 'api/DeviceRequest';
import {
  deviceKeys,
  partKeys,
  sectionKeys,
  zoneKeys,
} from 'api/quires/queryKeys';
import { BaseResp } from 'api/response';
import DeviceSearchBox from 'components/searchbox/DeviceSearchBox';
import DeviceInfo from 'model/DeviceInfo';
import { EquipmentInfo } from 'model/Equipment';
import Factory from 'model/Factory';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import {
  DEVICE_MODAL_TYPE,
  MV_CODE,
  ROLE_TYPE,
  STATISTICS_TYPE,
  YN_TYPE,
} from 'types/enumType';
import { getDeviceIdByType } from 'util/commonUtil';
import { decryptString } from 'util/encryptUtil';

import DeviceModal from './DeviceModal';
import DeviceTable from './DeviceTable';
import EquipModal from './EquipModal';
import EquipTable from './EquipTable';

export type ModalPropType = {
  type: DEVICE_MODAL_TYPE;
  device: DeviceInfo | EquipmentInfo;
  isOpen: boolean;
};

export default function Device() {
  useRecoilValue(userInfoState);
  const userInfo = useRecoilValue(userInfoState);
  const role = decryptString(userInfo.role);
  const isAdmin = role === ROLE_TYPE.ADMIN;
  const deviceTypeList = [
    DEVICE_MODAL_TYPE.PART,
    DEVICE_MODAL_TYPE.AREA,
    DEVICE_MODAL_TYPE.SECTION,
    DEVICE_MODAL_TYPE.DEVICE,
  ];
  const [searchRequest, setSearchRequest] = useState<DeviceSearchRequest>({
    factoryId: '',
    partId: 0,
    zoneId: 0,
    sectionId: 0,
    equipId: 0,
  });
  const [modalProps, setModalProps] = useState<ModalPropType[]>([
    {
      type: DEVICE_MODAL_TYPE.PART,
      device: new DeviceInfo(0),
      isOpen: false,
    },
    {
      type: DEVICE_MODAL_TYPE.AREA,
      device: new DeviceInfo(0),
      isOpen: false,
    },
    {
      type: DEVICE_MODAL_TYPE.SECTION,
      device: new DeviceInfo(0),
      isOpen: false,
    },
    {
      type: DEVICE_MODAL_TYPE.DEVICE,
      device: new EquipmentInfo(0),
      isOpen: false,
    },
  ]);

  // 설비 목록 api
  const {
    data: partList,
    refetch: refetchPart,
    remove: removePart,
  } = useQuery(
    partKeys.list(searchRequest.factoryId),
    () => getPartList(searchRequest),
    {
      enabled:
        role !== ROLE_TYPE.ADMIN
          ? true
          : parseInt(searchRequest.factoryId!, 10) > 0,
    },
  );
  const {
    data: zoneList,
    refetch: refetchZone,
    remove: removeZone,
  } = useQuery(
    zoneKeys.list(searchRequest.partId!),
    () => getZoneList(searchRequest),
    { enabled: searchRequest.partId! > 0 },
  );
  const {
    data: sectionList,
    refetch: refetchSection,
    remove: removeSection,
  } = useQuery(
    sectionKeys.list(searchRequest.zoneId!),
    () => getSectionList(searchRequest),
    { enabled: searchRequest.zoneId! > 0 },
  );
  const {
    data: deviceList,
    refetch: refetchDevice,
    remove: removeDevice,
  } = useQuery(
    deviceKeys.list(searchRequest.sectionId!),
    () => getEquipList(searchRequest),
    { enabled: searchRequest.sectionId! > 0 },
  );

  // 설비 등록/수정 mutation
  const deviceRegisterMutation = useRegisterDeviceMutation();
  const deviceEditMutation = useEditDeviceMutation();
  const equipRegisterMutation = useRegisterEquipMutation();
  const equipEditMutation = useEditEquipMutation();

  // 목록 refetch useEffect
  useEffect(() => {
    if (partList && partList?.list?.length > 0) {
      setSearchRequest((prev) => ({
        ...prev,
        partId: prev.partId! > 0 ? prev.partId : partList?.list[0].id,
      }));
    }
  }, [partList]);

  useEffect(() => {
    if (!zoneList) {
      return;
    }

    let zoneId = 0;
    if (zoneList.list?.length > 0) {
      zoneId =
        searchRequest.zoneId! > 0
          ? searchRequest.zoneId!
          : zoneList?.list[0].id;
    }

    setSearchRequest((prev) => ({
      ...prev,
      zoneId,
    }));
  }, [zoneList, searchRequest.partId]);

  useEffect(() => {
    if (!sectionList) {
      return;
    }

    let sectionId = 0;
    if (sectionList.list?.length > 0) {
      sectionId =
        searchRequest.sectionId! > 0
          ? searchRequest.sectionId!
          : sectionList?.list[0].id;
    }

    setSearchRequest((prev) => ({
      ...prev,
      sectionId,
    }));
  }, [sectionList, searchRequest.sectionId]);

  useEffect(() => {
    if (!deviceList) {
      return;
    }

    let equipId = 0;
    if (deviceList?.list?.length > 0) {
      equipId =
        searchRequest.equipId! > 0
          ? searchRequest.equipId!
          : deviceList?.list[0].id;
    }

    setSearchRequest((prev) => ({
      ...prev,
      equipId,
    }));
  }, [deviceList, searchRequest.equipId]);

  const refetchByType = (type: DEVICE_MODAL_TYPE) => {
    switch (type) {
      case DEVICE_MODAL_TYPE.PART:
        refetchPart();
        break;
      case DEVICE_MODAL_TYPE.AREA:
        refetchZone();
        break;
      case DEVICE_MODAL_TYPE.SECTION:
        refetchSection();
        break;
      default:
        refetchDevice();
    }
  };

  const getListByType = (type: DEVICE_MODAL_TYPE) => {
    switch (type) {
      case DEVICE_MODAL_TYPE.PART:
        return partList?.list ?? [];
      case DEVICE_MODAL_TYPE.AREA:
        return zoneList?.list ?? [];
      case DEVICE_MODAL_TYPE.SECTION:
        return sectionList?.list ?? [];
      default:
        return deviceList?.list ?? [];
    }
  };

  const setDeviceIdByType = (type: DEVICE_MODAL_TYPE, id: number) => {
    switch (type) {
      case DEVICE_MODAL_TYPE.PART:
        setSearchRequest({
          ...searchRequest,
          partId: id,
          zoneId: 0,
          sectionId: 0,
          equipId: 0,
        });
        removeZone();
        removeSection();
        removeDevice();
        break;

      case DEVICE_MODAL_TYPE.AREA:
        setSearchRequest({
          ...searchRequest,
          zoneId: id,
          sectionId: 0,
          equipId: 0,
        });
        removeSection();
        removeDevice();
        break;
      case DEVICE_MODAL_TYPE.SECTION:
        setSearchRequest({ ...searchRequest, sectionId: id, equipId: 0 });
        removeDevice();
        break;
      default:
        setSearchRequest({ ...searchRequest, equipId: id });
    }
  };

  const getDeviceInfoByType = (type: DEVICE_MODAL_TYPE, id: number) => {
    switch (type) {
      case DEVICE_MODAL_TYPE.PART:
        return partList?.list.find((part) => part.id === id);
      case DEVICE_MODAL_TYPE.AREA:
        return zoneList?.list.find((zone) => zone.id === id);
      case DEVICE_MODAL_TYPE.SECTION:
        return sectionList?.list.find((section) => section.id === id);
      default:
        return deviceList?.list.find((device) => device.id === id);
    }
  };

  const getChangedModalPropByType = (
    prev: ModalPropType[],
    deviceType: DEVICE_MODAL_TYPE,
    id: number,
  ): ModalPropType[] => {
    prev.forEach((modalProp) => {
      if (modalProp.type === deviceType) {
        modalProp.isOpen = !modalProp.isOpen;
        if (id > 0) {
          modalProp.device = getDeviceInfoByType(deviceType, id)!;
        } else {
          modalProp.device = new DeviceInfo(id);
        }
      }
    });

    return prev;
  };

  const isMVEquip = (zoneId: number | undefined) => {
    // id보고 M이 들어갔으면 Y : N
    const zoneCode = zoneList?.list.find((zone) => zone.id === zoneId)?.code;
    return zoneCode === MV_CODE.Z101 || zoneCode === MV_CODE.Z201;
  };

  const onClose = (type: DEVICE_MODAL_TYPE) => {
    setModalProps((prev) => [...getChangedModalPropByType(prev, type, 0)]);
  };
  const onSelect = (type: DEVICE_MODAL_TYPE, id: number) => {
    setDeviceIdByType(type, id);
  };
  const onRegister = (type: DEVICE_MODAL_TYPE) => {
    setModalProps((prev) => [...getChangedModalPropByType(prev, type, -1)]);
  };
  const onClickDetail = (type: DEVICE_MODAL_TYPE, id: number) => {
    setModalProps((prev) => [...getChangedModalPropByType(prev, type, id)]);
  };

  const handleSearchSubmit = (
    factoryInfo: Factory | null,
    event?: FormEvent,
  ) => {
    if (event) {
      event.preventDefault();
    }

    setSearchRequest((prev) => ({
      ...prev,
      factoryId: (factoryInfo?.id ?? '').toString(),
      partId: 0,
      zoneId: 0,
      sectionId: 0,
      equipId: 0,
    }));
    setTimeout(() => {
      removePart();
      removeZone();
      removeSection();
      removeDevice();

      refetchPart();
    });
  };

  const handleOnSuccessByType = (resp: BaseResp, type: DEVICE_MODAL_TYPE) => {
    if (resp.code === 200) {
      refetchByType(type);
      onClose(type);
    } else {
      alert(resp.message);
    }
  };

  const handleDeviceSubmit = (formData: DeviceInfoRequest) => {
    if (formData.id < 0) {
      // 등록
      deviceRegisterMutation.mutate(
        { ...formData, ...searchRequest },
        {
          onSuccess: (resp) => {
            handleOnSuccessByType(resp, formData.type);
          },
        },
      );
    } else if (formData.id > 0) {
      // 수정
      deviceEditMutation.mutate(
        { ...formData, ...searchRequest },
        {
          onSuccess: (resp) => {
            handleOnSuccessByType(resp, formData.type);
          },
        },
      );
    }
  };

  const handleEquipSubmit = (formData: EquipmentInfoRequest) => {
    if (formData.id < 0) {
      // 등록
      equipRegisterMutation.mutate(
        {
          ...formData,
          sectionId: searchRequest.sectionId!,
          manage: isMVEquip(searchRequest.zoneId) ? YN_TYPE.Y : YN_TYPE.N,
        },
        {
          onSuccess: (resp) => {
            handleOnSuccessByType(resp, formData.type);
          },
        },
      );
    } else if (formData.id > 0) {
      // 수정
      equipEditMutation.mutate(
        { ...formData, sectionId: searchRequest.sectionId! },
        {
          onSuccess: (resp) => {
            handleOnSuccessByType(resp, formData.type);
          },
        },
      );
    }
  };

  const isAvailableDevice = (type: DEVICE_MODAL_TYPE) => {
    switch (type) {
      case DEVICE_MODAL_TYPE.SECTION:
        if (!searchRequest.zoneId) {
          return false;
        }
        return searchRequest.zoneId > 0;
      case DEVICE_MODAL_TYPE.DEVICE:
        if (!searchRequest.sectionId) {
          return false;
        }
        return searchRequest.sectionId > 0;
      default:
        return false;
    }
  };

  const { pathname } = useLocation();
  const { mutate } = useUpdateStatisticsMutation();

  useEffect(() => {
    mutate(STATISTICS_TYPE.EQUIP_MANAGE, {});
  }, [pathname]);

  return (
    <>
      {role === ROLE_TYPE.ADMIN && (
        <DeviceSearchBox
          searchRequest={searchRequest}
          isLoading={false}
          onSubmit={handleSearchSubmit}
        />
      )}

      <div className={'cont-wrap device-wrap'}>
        {Children.toArray(
          deviceTypeList.map((type) =>
            type === DEVICE_MODAL_TYPE.DEVICE ? (
              <EquipTable
                type={type}
                list={getListByType(type)}
                isAdmin={isAdmin}
                isAvailableDevice={isAvailableDevice}
                onRegister={onRegister}
                onClickDetail={onClickDetail}
              />
            ) : (
              <DeviceTable
                type={type}
                list={getListByType(type)}
                deviceId={getDeviceIdByType(searchRequest, type)}
                isAdmin={isAdmin}
                isAvailableDevice={isAvailableDevice}
                onSelect={onSelect}
                onRegister={onRegister}
                onClickDetail={onClickDetail}
              />
            ),
          ),
        )}
      </div>
      {modalProps &&
        modalProps.map(({ type, isOpen, device }: ModalPropType) =>
          type === DEVICE_MODAL_TYPE.DEVICE ? (
            <EquipModal
              key={type + 1}
              type={type}
              equipment={device}
              isOpen={isOpen}
              isAdmin={isAdmin}
              onSubmit={handleEquipSubmit}
              onClose={() => onClose(type)}
            />
          ) : (
            <DeviceModal
              key={type + 1}
              type={type}
              device={device}
              isOpen={isOpen}
              onSubmit={handleDeviceSubmit}
              onClose={() => onClose(type)}
            />
          ),
        )}
    </>
  );
}
